<script setup lang="ts">
import { computed } from 'vue'

import type { SelectionRow } from '@/helpers/selectionRows'

import PriceDisplay from '@/components/elements/PriceDisplay.vue'

import SelectionLines from './SelectionLines.vue'

export type SelectionSummaryStepProps = {
  row: SelectionRow
  first?: boolean
  skippedLabel?: string
}

const props = withDefaults(defineProps<SelectionSummaryStepProps>(), {
  first: false,
  skippedLabel: 'Skipped',
})

const emit = defineEmits<{
  edit: [resolverId: string, type?: string]
}>()

const hasPick = computed(() => Boolean(props.row.subject || props.row.detail) || props.row.price !== undefined)

/** The API leaves the heading empty when the step has neither a `summary_title` nor a subject. */
const editLabel = computed(() => `Edit ${props.row.heading || props.row.subject || 'selection'}`)
</script>

<template>
  <li class="selection-step">
    <div aria-hidden="true" class="nodes">
      <span class="connector" :class="{ hidden: first }" />
      <span class="node" />
      <span class="connector grow" />
    </div>

    <div class="step-content" :class="{ 'no-heading': !row.heading }">
      <div v-if="row.heading || row.editable" class="prompt-row">
        <h3 v-if="row.heading" class="prompt">{{ row.heading }}</h3>

        <button
          v-if="row.editable"
          :aria-label="editLabel"
          class="edit"
          type="button"
          @click="emit('edit', row.resolverId, row.editType)"
        >
          Edit
        </button>
      </div>

      <div class="resolution">
        <p v-if="row.skipped" class="skipped">{{ props.skippedLabel }}</p>

        <template v-else>
          <div v-if="hasPick" class="pick">
            <div class="pick-header" :class="{ priced: row.price !== undefined }">
              <div class="pick-identity">
                <span v-if="row.subject" class="pick-name">{{ row.subject }}</span>
                <span v-if="row.detail" class="pick-detail">{{ row.detail }}</span>
              </div>

              <span v-if="row.price !== undefined" class="pick-price">
                <PriceDisplay :value="row.price" />
              </span>
            </div>

            <SelectionLines v-if="row.lines.length > 0" :lines="row.lines" />
          </div>

          <SelectionLines v-else-if="row.lines.length > 0" :lines="row.lines" />
        </template>
      </div>
    </div>
  </li>
</template>

<style scoped>
.selection-step {
  display: flex;
  align-items: flex-start;
  gap: var(--padding-compact);
  padding-inline: var(--padding-extra-tight);
}

.nodes {
  display: flex;
  flex-direction: column;
  align-items: center;
  align-self: stretch;
  width: var(--padding-tight);
}

.connector {
  height: var(--padding-compact);
  width: 1px;
  background-color: var(--card-border);

  &.hidden {
    background-color: transparent;
  }

  &.grow {
    flex: 1;
    height: auto;
  }
}

.node {
  width: var(--padding-tight);
  height: var(--padding-tight);
  border-radius: 50%;
  background-color: var(--brand-primary-400);
}

.step-content {
  flex: 1;
  min-width: 0;
  padding-block: var(--space-0-75);

  /* With no question to head it, the resolution takes the row Edit would have had to itself. */
  &.no-heading {
    display: flex;
    align-items: flex-start;
    gap: var(--padding-compact);

    .resolution {
      flex: 1;
      min-width: 0;
    }

    .prompt-row {
      order: 1;
      padding-bottom: 0;
    }
  }
}

.prompt-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--padding-tight);
  font-size: var(--font-size-body-tiny);
  line-height: var(--line-height-body);
  padding-bottom: var(--padding-tight);
}

.prompt {
  flex: 1;
  min-width: 0;
  font-family: var(--font-family-body);
  font-size: inherit;
  font-weight: var(--font-weight-regular);
  line-height: inherit;
  color: var(--text-primary);
}

.edit {
  font-size: var(--font-size-body-tiny);
  flex-shrink: 0;
  border: none;
  background: none;
  padding: 0;
  color: var(--text-link);
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    color: var(--text-link-hover);
    text-decoration: underline;
  }
}

.resolution {
  display: flex;
  flex-direction: column;
  gap: var(--padding-tight);
  font-size: var(--font-size-body-small);
  line-height: var(--line-height-body);
  color: var(--text-primary);
}

.pick-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--padding-tight);

  &.priced {
    padding-left: var(--padding-tight);

    .pick-name {
      font-weight: var(--font-weight-semibold);
    }
  }
}

.pick-identity {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.pick-detail {
  color: var(--text-secondary);
}

.pick-price {
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.skipped {
  padding-left: var(--padding-tight);
  font-style: italic;
  color: var(--text-secondary);
}
</style>
